import React, { type JSX } from 'react'
import { Link } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { Header } from '@/components/layout/Header'
import { type NavItemConfig } from '@/components/layout/NavItem'
import { NavEntries } from '@/components/layout/NavMenu'
import { useAuth } from '@/hooks/useAppAuth'
import { getFeatureNavItems, getDevFeatureNavItems } from '@/features/registry'
import { SupportBundleContainer } from '@/features/support-bundles/SupportBundleContainer'
import { LayoutDashboard } from 'lucide-react'

interface AppLayoutProps {
  children: React.ReactNode
}

const coreNavItems: NavItemConfig[] = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
]

function useNavItems(): { navigationItems: Array<NavItemConfig>; developmentNavItems: Array<NavItemConfig>; } {
  const navigationItems = [...coreNavItems, ...getFeatureNavItems()]
  const developmentNavItems = getDevFeatureNavItems()
  return { navigationItems, developmentNavItems }
}

// (UserInfoCard + AppSidebarContent removed — both were declared but only
// AppSidebarContent referenced UserInfoCard, and AppSidebarContent itself
// was never referenced. Navigation rendering lives in HeaderNav below.)

const HeaderNav = (): JSX.Element => {
  const { navigationItems, developmentNavItems } = useNavItems()
  return (
    <>
      <NavEntries items={navigationItems} />
      {developmentNavItems.length > 0 && (
        <>
          <div className="mx-2 h-6 w-px bg-border" />
          <NavEntries items={developmentNavItems} />
        </>
      )}
    </>
  )
}

export const AppLayout = ({ children }: AppLayoutProps): JSX.Element => {
  const { isLoading, isAuthenticated } = useAuth()
  const { navigationItems, developmentNavItems } = useNavItems()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-2xl font-semibold">PIPE-FLO Connect</div>
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </div>
    )
  }

  return (
    <AppShell
      header={
        <Header
          logo={
            <Link to="/" className="flex items-center space-x-2">
              <h1 className="text-xl font-bold">PIPE-FLO Connect</h1>
            </Link>
          }
          nav={isAuthenticated ? <HeaderNav /> : undefined}
        />
      }
      sidebar={undefined}
    >
      {/* Mobile Navigation */}
      {isAuthenticated && (
        <nav className="lg:hidden border-b p-2">
          <div className="flex flex-wrap gap-2">
            <NavEntries items={navigationItems} />
            {developmentNavItems.length > 0 && (
              <>
                <div className="w-full border-t my-2" />
                <NavEntries items={developmentNavItems} />
              </>
            )}
          </div>
        </nav>
      )}
      {children}
      {/* Mounted here rather than in the editor so the shortcut works on every
          route. "The import wizard hung" and "the dashboard won't load" are the
          reports that currently cannot be reproduced; off the editor the bundle
          simply carries no model. */}
      <SupportBundleContainer />
    </AppShell>
  )
}
